import { CAMPAIGN_IMPROVE_ITEM_DEFINITIONS } from '@/constants/campaign-improve-items';
import { isProofCurrent } from '@/lib/utils/proof-readiness';
import type {
  CampaignImproveItemId,
  CampaignImproveItemStatus,
  LocationCampaignImproveItem,
} from '@/types/campaign-improve';
import type { ProofMetric, ReviewDue } from '@/types/customer-ops';
import type { Location } from '@/types/location';

export type CampaignImproveItemRow = {
  id: string;
  location_id: string;
  item_id: CampaignImproveItemId;
  status: CampaignImproveItemStatus;
  owner: string | null;
  completed_at: string | null;
  notes: string;
  created_at: string;
  updated_at: string;
};

export interface CampaignImproveDerivationContext {
  location: Location;
  proofRows: ProofMetric[];
  reviewDueRows: ReviewDue[];
}

export function isCampaignImproveItemClosed(status: CampaignImproveItemStatus): boolean {
  return status === 'complete' || status === 'waived';
}

function isCi1DerivedComplete(ctx: CampaignImproveDerivationContext): boolean {
  return Boolean(ctx.location.baseline?.trim());
}

function isCi3DerivedComplete(ctx: CampaignImproveDerivationContext): boolean {
  const caseStudyUrl = ctx.location.case_study_url?.trim();
  if (!caseStudyUrl) return false;

  return ctx.proofRows.some(
    (row) =>
      isProofCurrent(row) &&
      row.location_ids.includes(ctx.location.id) &&
      row.source_ref.trim() === caseStudyUrl
  );
}

function isCi4DerivedComplete(ctx: CampaignImproveDerivationContext): boolean {
  return ctx.reviewDueRows.some(
    (row) =>
      row.location_id === ctx.location.id &&
      row.review_type === 'improvement_campaign_readout' &&
      row.status === 'completed'
  );
}

function isItemDerivedComplete(itemId: CampaignImproveItemId, ctx: CampaignImproveDerivationContext): boolean {
  switch (itemId) {
    case 'CI1':
      return isCi1DerivedComplete(ctx);
    case 'CI2':
      return false;
    case 'CI3':
      return isCi3DerivedComplete(ctx);
    case 'CI4':
      return isCi4DerivedComplete(ctx);
    default:
      return false;
  }
}

export function resolveEffectiveCampaignImproveStatus(
  itemId: CampaignImproveItemId,
  storedStatus: CampaignImproveItemStatus,
  ctx: CampaignImproveDerivationContext
): { effectiveStatus: CampaignImproveItemStatus; isDerivedComplete: boolean } {
  const derivedComplete = isItemDerivedComplete(itemId, ctx);

  if (storedStatus === 'blocked' || storedStatus === 'waived') {
    return { effectiveStatus: storedStatus, isDerivedComplete: false };
  }

  if (storedStatus === 'complete') {
    return { effectiveStatus: 'complete', isDerivedComplete: false };
  }

  if (derivedComplete) {
    return { effectiveStatus: 'complete', isDerivedComplete: true };
  }

  return { effectiveStatus: storedStatus, isDerivedComplete: false };
}

export function mergeLocationCampaignImproveItems(
  locationId: string,
  rows: CampaignImproveItemRow[],
  ctx: CampaignImproveDerivationContext
): LocationCampaignImproveItem[] {
  const rowByItemId = new Map(rows.map((row) => [row.item_id, row]));

  return CAMPAIGN_IMPROVE_ITEM_DEFINITIONS.map((definition) => {
    const row = rowByItemId.get(definition.itemId);
    const storedStatus = row?.status ?? 'not_started';
    const { effectiveStatus, isDerivedComplete } = resolveEffectiveCampaignImproveStatus(
      definition.itemId,
      storedStatus,
      ctx
    );

    return {
      id: row?.id ?? null,
      location_id: locationId,
      item_id: definition.itemId,
      status: storedStatus,
      effectiveStatus,
      isDerivedComplete,
      owner: row?.owner ?? definition.defaultOwner,
      completed_at: row?.completed_at ?? null,
      notes: row?.notes ?? '',
      label: definition.label,
      description: definition.description,
      defaultOwner: definition.defaultOwner,
      supportsAutoComplete: definition.supportsAutoComplete,
      created_at: row?.created_at ?? null,
      updated_at: row?.updated_at ?? null,
    };
  });
}

export function computeCampaignImproveProgress(items: LocationCampaignImproveItem[]): {
  completeCount: number;
  totalCount: number;
  percent: number;
} {
  const totalCount = items.length;
  const completeCount = items.filter((item) => isCampaignImproveItemClosed(item.effectiveStatus)).length;
  const percent = totalCount === 0 ? 0 : Math.round((completeCount / totalCount) * 100);

  return { completeCount, totalCount, percent };
}

export function resolveCompletedAtForCampaignImproveStatus(
  status: CampaignImproveItemStatus,
  previousCompletedAt: string | null
): string | null {
  if (status === 'complete') {
    return previousCompletedAt ?? new Date().toISOString();
  }
  if (status === 'waived') {
    return previousCompletedAt;
  }
  return null;
}

export function locationHasCampaignImproveActivity(location: Location): boolean {
  return Boolean(
    location.campaign_start ||
      location.campaign_end ||
      location.baseline?.trim() ||
      location.case_study_url?.trim() ||
      location.campaign_status
  );
}
